import { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react'
import PageContainer from '../../components/layout/PageContainer'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import FilterModal from '../../components/common/FilterModal'
import Searchbox from '../../components/common/Searchbox'
import { subscriptionService } from '../../services/api/subscriptionService'
import { formatDate } from '../../utils/formatters'

const SubscriptionAndBillings = () => {
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [totalResults, setTotalResults] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [filtersQuery, setFiltersQuery] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)

  const filterFields = [
    {
      name: 'subscriptionStatus',
      type: 'select',
      label: 'Subscription Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Expiring', value: 'expiring' },
        { label: 'Cancelled', value: 'canceled' },
        { label: 'Pending', value: 'pending' },
      ],
      placeholder: 'All',
    },
    {
      name: 'sortBy',
      type: 'select',
      label: 'Sort By',
      options: [
        { label: 'Newest first', value: 'createdAt:desc' },
        { label: 'Oldest first', value: 'createdAt:asc' },
      ],
      placeholder: 'Default',
    },
    {
      name: 'planId',
      type: 'text',
      label: 'Plan ID',
      placeholder: 'Enter plan ID',
    },
    {
      name: 'userId',
      type: 'text',
      label: 'User ID',
      placeholder: 'Enter user ID',
    },
  ]

  const fetchSubscriptions = useCallback(
    async (p = page, l = limit) => {
      setLoading(true)
      try {
        let q = `/subscriptions?page=${p}&limit=${l}`
        if (search) q += `&search=${encodeURIComponent(search)}`
        if (filtersQuery) q += `&${filtersQuery.replace(/^\?/, '')}`

        const response = await subscriptionService.list({
          page: p,
          limit: l,
          search: search || undefined,
          ...(filtersQuery ? Object.fromEntries(new URLSearchParams(filtersQuery)) : {}),
        })

        if (response && response.results) {
          const {
            results = [],
            totalResults: respTotal = 0,
            totalPages: respPages = Math.max(1, Math.ceil(respTotal / l)),
            page: respPage = p,
          } = response;

          const formattedData = (results || []).map((subscription) => ({
            id: subscription._id,
            userName: subscription.userName || 'N/A',
            profilePicture: subscription.profilePicture,
            planType: subscription.planType || 'N/A',
            billingCycle:
              subscription.billingCycle === 'month'
                ? 'Monthly'
                : subscription.billingCycle === 'year'
                ? 'Yearly'
                : subscription.billingCycle || 'Monthly',
            nextBillingDate: subscription.nextBillingDate
              ? formatDate(subscription.nextBillingDate)
              : 'N/A',
            amount: subscription.amount
              ? `$${Number(subscription.amount).toLocaleString()}`
              : '$0',
            currency: subscription.currency || 'USD',
            status: subscription.status
              ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)
              : 'Inactive',
            rawData: subscription,
          }))

          setTableData(formattedData)
          setTotalResults(Number(respTotal || formattedData.length || 0))
          setTotalPages(Number(respPages))
          setPage(Number(respPage))
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error)
        setTableData([])
        setTotalResults(0)
        setTotalPages(1)
      } finally {
        setLoading(false)
      }
    },
    [limit, page, search, filtersQuery]
  )

  useEffect(() => {
    fetchSubscriptions(page, limit)
  }, [fetchSubscriptions, page, limit, search, filtersQuery])

  const handlePageChange = (newPage) => {
    if (typeof newPage === 'number' && Number.isFinite(newPage) && newPage > 0) {
      setPage(newPage)
    }
  }

  const handleSearch = (value) => {
    setPage(1)
    setSearch(value)
  }

  const handleFilterApply = (queryString) => {
    const cleaned = (queryString || '').replace(/^\?/, '')
    setPage(1)
    setFiltersQuery(cleaned)
  }

  const getStatusStyles = (status) => {
    const subscriptionStatusStyles = {
      Active: { text: 'text-green-700', bg: 'bg-green-50' },
      Inactive: { text: 'text-red-700', bg: 'bg-red-50' },
      Expiring: { text: 'text-yellow-700', bg: 'bg-yellow-50' },
      Cancelled: { text: 'text-gray-700', bg: 'bg-gray-50' },
      Pending: { text: 'text-blue-700', bg: 'bg-blue-50' },
    }
    return subscriptionStatusStyles[status] || { text: 'text-gray-700', bg: 'bg-gray-50' }
  }

  return (
    <PageContainer
      title="Subscriptions & Billings"
      subtitle="Manage subscriptions and billing information"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Total Subscriptions</p>
          <p className="text-xl font-semibold text-text-dark mt-2">{totalResults}</p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Active Subscriptions</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {tableData.filter((item) => item.status === 'Active').length}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Expiring Soon</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {tableData.filter((item) => item.status === 'Expiring').length}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Current Page</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {page} / {Math.ceil(totalResults / limit) || 1}
          </p>
        </div>
      </div>

      <div className="mt-4 bg-white border border-border-light p-4 rounded-xl">
        <div className="flex flex-col md:flex-row items-center py-4 justify-between gap-2">
          <p className="text-sm font-semibold text-text-dark">Subscription & Billings</p>
          <div className="flex items-center gap-2 justify-end w-full md:w-[700px]">
            <div className="relative flex-1">
              <Searchbox
                placeholder="Search subscriptions by user"
                onSearch={handleSearch}
              />
            </div>

            <button
              onClick={() => setFilterOpen(true)}
              className={`flex items-center gap-2 px-4 rounded-lg border border-border-light h-10 ${
                filtersQuery ? 'text-primary border-primary' : 'text-text-dark'
              }`}
              type="button"
            >
              <span>Filters</span>
              <Icon icon="mdi:filter-outline" className={filtersQuery ? 'text-primary' : ''} />
              {filtersQuery && (
                <Icon
                  icon="mdi:close-circle"
                  className="text-red-500 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    setFiltersQuery('')
                    setPage(1)
                  }}
                />
              )}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-border-light">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    User Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Plan Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Billing Cycle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Next Billing Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light">
                {tableData.length > 0 ? (
                  tableData.map((row) => {
                    const statusStyles = getStatusStyles(row.status)
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                              {row.userName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-text-dark">{row.userName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-dark">{row.planType}</td>
                        <td className="px-4 py-3 text-sm text-text-dark">{row.billingCycle}</td>
                        <td className="px-4 py-3 text-sm text-text-dark">
                          {row.nextBillingDate}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-text-dark">
                          {row.amount}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full py-1 px-3 text-xs font-medium ${statusStyles.text} ${statusStyles.bg}`}
                          >
                            <Icon icon="mdi:circle" className="text-xs" />
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Icon
                        icon="mdi:credit-card-outline"
                        className="mx-auto mb-2 text-4xl text-gray-300"
                      />
                      <p className="text-sm font-semibold text-text-dark">
                        No subscriptions found
                      </p>
                      <p className="text-xs text-text-secondary">
                        Subscriptions will appear here when created
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && totalResults > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-light">
            <p className="text-sm text-text-secondary">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalResults)} of{' '}
              {totalResults} results
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-border-light rounded-lg text-sm text-text-dark disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-text-dark">
                Page {page} of {Math.ceil(totalResults / limit)}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= Math.ceil(totalResults / limit)}
                className="px-3 py-1 border border-border-light rounded-lg text-sm text-text-dark disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fields={filterFields}
        onApply={(q) => handleFilterApply(q)}
      />
    </PageContainer>
  )
}

export default SubscriptionAndBillings
